import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ActiveTestQueuedStateService,
  AGENT_MODEL,
  AGENT_TYPE,
  ApiService,
  Device,
  DialogService,
  EntityCacheProvider,
  FormatService,
  LmapService,
  SeverityThreshold,
  Site,
  TestAssembleService,
  TestServerConfigService,
  TransformationService,
  TWAMP_MODE
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import {
  TwampConfigFieldMapping as ConfigFieldMapping,
  IPAddressValue,
  vlanConfigValue,
  TX_INTERVAL,
  JMEP_TX_INTERVAL
} from './components/models/model';
@Injectable({
  providedIn: 'root'
})
export class TwampTestService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  exceedsIPRange: boolean;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  formatService: FormatService;
  lmapService: LmapService;
  activeTestQueuedStateService: ActiveTestQueuedStateService;
  serverConfigService: TestServerConfigService;
  deviceModel = 'vPMA';
  testTaskName = 'twamp-initiator';
  selectedOriginator: Device;
  selectedDestination: any;
  testAssembleService: TestAssembleService;
  private CODEC_URL = '/monitor/MonitorManager/v1/codecs';
  private readonly unsubscribe: Subject<void> = new Subject();
  tabDataChangeEvents = new Subject<number>();
  monitorId: string;
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    testAssembleService: TestAssembleService,
    lmapService: LmapService,
    formatService: FormatService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.formatService = formatService;
    this.lmapService = lmapService;
    this.serverConfigService = serverConfigService;
    this.testAssembleService = testAssembleService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadCodecOptions(callbackFn: (any) => any): void {
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(this.CODEC_URL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          callbackFn(response);
        },
        error => {
          callbackFn(error);
        },
        () => {}
      );
  }

  //eslint-disable-next-line
  getThresholdRequestObject(): any {
    const thresholdObject = this.output.networkThreshold;
    const networkThresholdEntity = [];
    Object.keys(thresholdObject).forEach(key => {
      const kpiThreshold = thresholdObject[key].kpiThreshold;
      const unit = thresholdObject[key].unit;
      const thresholdValue =
        unit === 'ms'
          ? thresholdObject[key].thresholdValue * 1000
          : thresholdObject[key].thresholdValue;
      if (kpiThreshold) {
        const kpiObject = {
          kpiname: kpiThreshold.kpiname,
          dimensions: kpiThreshold.dimensions,
          expression: kpiThreshold.expression,
          severityThresholds: this.getSeverityThresholdObject(thresholdValue)
        };
        networkThresholdEntity.push(kpiObject);
      }
    });
    return networkThresholdEntity;
  }

  getSeverityThresholdObject(thresholdValue: number): Array<SeverityThreshold> {
    //assign with empty when no site defined for agent
    const siteId = this.output.siteId || '';
    const staticValues = siteId
      ? `${siteId},${this.output.monitorid}`
      : `9999,${this.output.monitorid}`;
    return [
      {
        staticValues: `${staticValues}`,
        severityThresholdPairs: [
          {
            severity: 'Critical',
            threshold: thresholdValue
          },
          {
            severity: 'Warning',
            threshold: thresholdValue
          }
        ]
      }
    ];
  }

  createLMAPObject(): any {
    const testPointA = this.selectedOriginator.getDevice();
    testPointA.id = testPointA.nameId;
    testPointA.name = '';
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(collectorURL);
    const events = this.createEvents(testSchedule);
    const controllerConfig = this.createControllerConfig(testPointA, events);
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }
  //eslint-disable-next-line
  createControllerConfig(testPointA, lmapified): any {
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    controllerConfig.test['test-list-data'] = {};
    controllerConfig.test['test-list-data']['tpInfo'] = [
      {
        type: 'primary',
        id: testPointA.uniqueId,
        ip: testPointA.mgmtIPaddress
      }
    ];
    controllerConfig.test['ipv6'] = 'no';
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['extended-config'][ConfigFieldMapping.SELECTED_TAB] =
      this.output[ConfigFieldMapping.SELECTED_TAB];
    controllerConfig.test['extended-config'][ConfigFieldMapping.REFLECTOR_UDP] =
      this.output[ConfigFieldMapping.REFLECTOR_UDP];
    controllerConfig.test['extended-config']['test'] = {
      nameOnProbe: 'twamp-initiator',
      selected: true,
      testType: 'twamp',
      testTypeLabel: 'TWAMP Initiator'
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[ConfigFieldMapping.ALIAS]
    };
    const testPointAInfo = [
      {
        tag: 'Test Point A',
        selected: true,
        name: testPointA.nameId,
        type: 'inventory',
        vendor: testPointA.model.vendor.toLowerCase(),
        version: testPointA.params?.find(item => item.name === 'version').value,
        model: testPointA.model.model,
        id: testPointA.uniqueId,
        ip: testPointA.mgmtIPaddress,
        port: testPointA.connections[0]?.uri.split(':')[2] || 443,
        testPortName:
          testPointA.phyId !== '' ? `${testPointA.nameId}_${testPointA.phyId.toUpperCase()}` : '',
        testPortPhyId: testPointA.phyId,
        connections: [
          {
            type: testPointA.connections[0].type,
            uri: testPointA.connections[0].uri
          }
        ]
      }
    ];
    const testPointBInfo = [];
    this.selectedDestination.forEach((item, index) => {
      testPointBInfo.push({
        tag: 'Test Point B',
        selected: false,
        name: 'Test Point B',
        type: 'inventory',
        vendor: 'viavi',
        version: '1.0',
        model: null,
        id: item.deviceData.id
      });
      // MV-24894 - Adding only two reflector info to show 'multiple' in test list page
      if (index <= 1) {
        controllerConfig.test['test-list-data']['tpInfo'].push({
          type: 'secondary',
          id: item.deviceData.id,
          ip: item.ipAddress?.label,
          model: item.deviceData.type === 'unmanaged' ? 'unmanaged' : item.deviceData?.model?.model
        });
      }
    });
    controllerConfig.test['extended-config']['testPoints'] = [...testPointAInfo, ...testPointBInfo];
    return controllerConfig;
  }
  // eslint-disable-next-line
  createEvents(testSchedule: any): void {
    const events = this.lmapService.addEvent(testSchedule, {
      name: this.output[ConfigFieldMapping.ALIAS] + '-results-periodic-pm-event',
      periodic: {
        interval: this.output[ConfigFieldMapping.PM_INTERVAL]
      }
    });
    return events;
  }
  createTestSchedule(collectorURL: string): void {
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testEventName = 'immediate';
    const testActionName = testScheduleName + '-results-periodic-pm';
    const destination = [testScheduleName + '-results-periodic-pm'];
    const action = this.lmapService.createAction(
      testScheduleName + '-action',
      this.testTaskName,
      this.getOptions(),
      destination
    );
    const firstAction = [];
    firstAction.push(action);
    const secondSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      firstAction
    );
    const action2 = this.lmapService.createAction(
      testActionName + '-action',
      'results',
      this.getSecondOptions(collectorURL)
    );
    const secondAction = [];
    delete action2['destination'];
    delete action2['tags'];
    secondAction.push(action2);
    const existingSchedule = this.lmapService.createSchedule(
      testActionName,
      testActionName + '-event',
      secondAction
    );
    const lmapified = this.lmapService.addSchedule(
      existingSchedule,
      this.lmapService.addSchedule(secondSchedule)
    );
    return lmapified;
  }
  getSecondOptions(collectorURL: string): any {
    const options = [
      {
        id: 'result-type',
        value: 'pm'
      },
      {
        id: 'channel',
        value: collectorURL
      }
    ];
    return options;
  }

  // eslint-disable-next-line
  getOptions(): any {
    let vlanConfig = [];
    const isIpv4 = this.output[ConfigFieldMapping.IP_ADDRESS_TYPE] === IPAddressValue.IP_V4;
    const isQTAgent = this.output[ConfigFieldMapping.AGENT_TYPE] === AGENT_MODEL.QT600;
    const isJMEPAgent = this.output[ConfigFieldMapping.AGENT_TYPE] === AGENT_MODEL.JMEP_1G;
    if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.Q_IN_Q) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        },
        {
          id: 'inner-vlan-id',
          value: this.output[ConfigFieldMapping.CVLAN_ID].toString()
        },
        {
          id: 'inner-vlan-priority',
          value: this.output[ConfigFieldMapping.CVLAN_PRIORITY].toString()
        },
        {
          id: 'tpid',
          value: this.output[ConfigFieldMapping.CVLAN_ETHER_TYPE]
        }
      ];
    } else if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.SINGLE_VLAN) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        }
      ];
    }
    let options = [
      {
        id: isIpv4 ? 'subnet' : 'prefix-length',
        value: this.output[ConfigFieldMapping.SUBNET]
      },
      {
        id: 'gateway',
        value: this.output[ConfigFieldMapping.GATEWAY]
      },
      {
        id: 'codec',
        value: this.output[ConfigFieldMapping.CODEC] ?? 'none'
      }
    ];
    options = [...options, ...vlanConfig];
    if (isQTAgent) {
      options = [
        ...options,
        ...[
          {
            id: 'phy-id',
            value: this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData.phyId
          }
        ]
      ];
    }
    if (isJMEPAgent) {
      options = [
        ...options,
        ...[
          {
            id: 'tx-interface',
            value: this.output[ConfigFieldMapping.TRANSMIT_INTERFACE]
          },
          {
            id: 'engine-type',
            value: AGENT_MODEL.JMEP_1G
          }
        ]
      ];
    }
    options = [
      ...options,
      ...[
        {
          id: 'enable-percentiles',
          value: this.output[ConfigFieldMapping.ENABLE_PERCENTILES]
            ? this.output[ConfigFieldMapping.ENABLE_PERCENTILES].toString()
            : 'false'
        }
      ]
    ];

    if (this.output[ConfigFieldMapping.KEEP_ALIVE]) {
      options = [
        ...options,
        ...[
          {
            id: 'twamp-keep-alive-interval',
            value: '300'
          }
        ]
      ];
    }

    if (this.output[ConfigFieldMapping.REBOOT]) {
      options = [
        ...options,
        ...[
          {
            id: 'persist',
            value: 'true'
          }
        ]
      ];
    }

    const twampReflector = this.selectedDestination.filter(
      reflector => reflector.dataInfo['twampMode'] === TWAMP_MODE.FULL
    );
    const twampLightReflector = this.selectedDestination.filter(
      reflector => reflector.dataInfo['twampMode'] === TWAMP_MODE.LIGHT
    );
    const twamppingReflector = this.selectedDestination.filter(
      reflector => reflector.dataInfo['twampMode'] === TWAMP_MODE.PING
    );

    if (twampReflector.length) {
      options.push({ id: 'twamp', value: this.getTwampConfiguration(twampReflector) });
      options.push({
        id: 'test-session-map',
        value: this.getTwampFullSessionMap(twampReflector)
      });
    }
    if (twampLightReflector.length || twamppingReflector.length) {
      const bothReflectors = twampLightReflector.concat(twamppingReflector);
      options.push({
        id: 'twamp-light',
        value: this.getTwampLightConfiguration(bothReflectors)
      });
      const lightPingOptions = this.getTwampLightPingOptions(bothReflectors);
      options = [...options, ...lightPingOptions];
    }
    return options;
  }

  getUniqueFlowName(destinationName: string, dscp: string): string {
    const testName = this.output[ConfigFieldMapping.ALIAS];
    const orginatorName =
      this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.deviceName;
    return `${testName}_${orginatorName}_${destinationName}_${dscp}`.replace(/\s/g, '');
  }

  getTwampFullSessionMap(twampReflector: any): any {
    const fullTwampSessionMap = {};
    if (this.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
      const commonFlows = this.output[ConfigFieldMapping.COMMON_FLOWS];
      for (let i = 0; i < twampReflector.length; i++) {
        for (let j = 0; j < commonFlows.length; j++) {
          //const flowName = `Flow ${i + 1}-${j + 1}`;
          const flowName = this.getUniqueFlowName(
            twampReflector[i].name.label,
            commonFlows[j].dscp
          );
          fullTwampSessionMap[flowName] = {
            'df-bit': JSON.parse(commonFlows[j].dfBit)
          };
        }
      }
    } else {
      for (let j = 0; j < twampReflector.length; j++) {
        const specificFlows =
          this.output[ConfigFieldMapping.SPECIFIC_FLOWS][twampReflector[j].ipAddress?.label];
        specificFlows.forEach(item => {
          //const flowName = item.name;
          const flowName = this.getUniqueFlowName(twampReflector[j].name.label, item.dscp);
          fullTwampSessionMap[flowName] = {
            'df-bit': JSON.parse(item.dfBit)
          };
        });
      }
    }
    return JSON.stringify(fullTwampSessionMap);
  }

  getTwampLightPingOptions(bothReflector: any): any {
    const sessionMapObj = {};
    const sessionGroupMapObj = {};

    if (this.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
      let index = 0;
      let endpointsIndex = 0;
      const commonFlows = this.output[ConfigFieldMapping.COMMON_FLOWS];
      for (let i = 0; i < bothReflector.length; i++) {
        const twamppingReflector = bothReflector[i]?.dataInfo['twampMode'] === TWAMP_MODE.PING;
        const listOfFlows = [];
        endpointsIndex++;
        for (let j = 0; j < commonFlows.length; j++) {
          const flowName = this.getUniqueFlowName(bothReflector[i].name.label, commonFlows[j].dscp);
          listOfFlows.push(flowName);
          sessionMapObj[index + 1] = {
            name: flowName,
            'payload-type': twamppingReflector ? 1 : 0,
            'df-bit': commonFlows[j].dfBit
          };
          index++;
        }
        sessionGroupMapObj[`My session ${endpointsIndex}`] = listOfFlows;
      }
    } else {
      const specificFlows = this.output[ConfigFieldMapping.SPECIFIC_FLOWS];
      let index = 0;
      let endpointsIndex = 0;
      for (let i = 0; i < bothReflector.length; i++) {
        const twamppingReflector = bothReflector[i]?.dataInfo['twampMode'] === TWAMP_MODE.PING;
        const ipaddress = bothReflector[i].ipAddress?.label;
        const listOfFlows = [];
        endpointsIndex++;
        if (ipaddress && specificFlows[ipaddress]) {
          specificFlows[ipaddress].forEach(item => {
            const flowName = this.getUniqueFlowName(bothReflector[i].name.label, item.dscp);
            listOfFlows.push(flowName);
            sessionMapObj[index + 1] = {
              name: flowName,
              'payload-type': twamppingReflector ? 1 : 0,
              'df-bit': item.dfBit
            };
            index++;
          });
          sessionGroupMapObj[`My session ${endpointsIndex}`] = listOfFlows;
        }
      }
    }

    const twampLightPingOption = [
      {
        id: 'session-map',
        value: JSON.stringify(sessionMapObj)
      },
      {
        id: 'session-group-map',
        value: JSON.stringify({ 'My session': sessionGroupMapObj })
      }
    ];
    return twampLightPingOption;
  }
  // eslint-disable-next-line
  getTwampConfiguration(twampReflector: any): any {
    const modePreferenceChain = [
      {
        priority: 1,
        mode: 'bits.unauthenticated'
      }
    ];
    const senderAdminState = {
      'session-sender-admin-state': true
    };
    const twampConfiguration = {};
    twampConfiguration['ietf-twamp:twamp'] = { 'twamp-client': { 'client-admin-state': true } };
    twampConfiguration['ietf-twamp:twamp']['twamp-client']['mode-preference-chain'] =
      modePreferenceChain;
    twampConfiguration['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'] =
      senderAdminState;
    twampConfiguration['ietf-twamp:twamp']['twamp-client']['twamp-session-sender'][
      'twamp-sender-test-session'
    ] = this.getSenderTestSessionConfig(twampReflector);
    twampConfiguration['ietf-twamp:twamp']['twamp-client']['twamp-client-ctrl-connection'] =
      this.getConnectionConfig(twampReflector);
    return JSON.stringify(twampConfiguration);
  }

  // eslint-disable-next-line
  getTwampLightConfiguration(twampLightReflector: Device[]): any {
    const twampConfiguration = {};
    twampConfiguration['ietf-twamp-light:twamp-light'] = {
      'twamp-light-session-sender': { 'sender-light-enable': true }
    };
    twampConfiguration['ietf-twamp-light:twamp-light']['twamp-light-session-sender'][
      'test-session'
    ] = this.getLightConnectionConfig(twampLightReflector);
    return JSON.stringify(twampConfiguration);
  }

  // eslint-disable-next-line
  getLightConnectionConfig(reflectors: any): any {
    const multipleConnections = [];
    let sessionId = 0;
    const commonFlows = this.output[ConfigFieldMapping.COMMON_FLOWS];
    for (let i = 0; i < reflectors.length; i++) {
      const payLoad = reflectors[i].deviceData.capabilities['twamp-light-reflector'] ? 0 : 1;
      if (this.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
        for (let j = 0; j < commonFlows.length; j++) {
          const config = {};
          config['session-id'] = sessionId + 1;
          config['test-session-enable'] = true;
          config['packet-padding-size'] = commonFlows[j].padding;
          config['session-authentication-mode'] = 'unauthenticated';
          config['interval'] = parseInt(commonFlows[j].txInterval);
          config['sender-ip'] = this.output[ConfigFieldMapping.ADDRESS];
          config['sender-udp-port'] = this.output[ConfigFieldMapping.BASE_UDP];
          config['reflector-ip'] = reflectors[i].ipAddress?.label;
          config['reflector-udp-port'] = reflectors[i].dataInfo?.['port']
            ? parseInt(reflectors[i].dataInfo.port)
            : this.output[ConfigFieldMapping.REFLECTOR_UDP];
          config['dscp'] = commonFlows[j].dscp;
          config['payload-type'] = payLoad;
          multipleConnections.push(config);
          sessionId++;
        }
      } else {
        const specificFlows =
          this.output[ConfigFieldMapping.SPECIFIC_FLOWS][reflectors[i].ipAddress?.label];
        for (let j = 0; j < specificFlows.length; j++) {
          const config = {};
          config['session-id'] = sessionId + 1;
          config['test-session-enable'] = true;
          config['packet-padding-size'] = specificFlows[j].padding;
          config['session-authentication-mode'] = 'unauthenticated';
          config['interval'] = parseInt(specificFlows[j].txInterval);
          config['sender-ip'] = this.output[ConfigFieldMapping.ADDRESS];
          config['sender-udp-port'] = this.output[ConfigFieldMapping.BASE_UDP];
          config['reflector-ip'] = reflectors[i].ipAddress?.label;
          config['reflector-udp-port'] = specificFlows[j].reflectorUDP;
          config['dscp'] = specificFlows[j].dscp;
          config['payload-type'] = payLoad;
          multipleConnections.push(config);
          sessionId++;
        }
      }
    }

    return multipleConnections;
  }
  // eslint-disable-next-line
  getConnectionConfig(twampReflector: any): any {
    const multipleConnections = [];
    const commonFlows = this.output[ConfigFieldMapping.COMMON_FLOWS];
    for (let i = 0; i < twampReflector.length; i++) {
      const connectionInfo = {};
      connectionInfo['ctrl-connection-name'] = `My session ${i + 1}`;
      connectionInfo['client-ip'] = this.output[ConfigFieldMapping.ADDRESS];
      connectionInfo['server-ip'] = twampReflector[i].ipAddress?.label;
      connectionInfo['server-tcp-port'] = twampReflector[i].dataInfo?.['port']
        ? parseInt(twampReflector[i].dataInfo.port)
        : this.output[ConfigFieldMapping.REFLECTOR_UDP];
      connectionInfo['dscp'] = 0;
      const config = [];
      if (this.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
        for (let j = 0; j < commonFlows.length; j++) {
          const flowName = this.getUniqueFlowName(
            twampReflector[i].name.label,
            commonFlows[j].dscp
          );
          config.push({
            'test-session-name': flowName,
            'sender-ip': this.output[ConfigFieldMapping.ADDRESS],
            'sender-udp-port': this.output[ConfigFieldMapping.BASE_UDP],
            'reflector-ip': twampReflector[i].ipAddress?.label,
            'reflector-udp-port': this.output[ConfigFieldMapping.REFLECTOR_UDP],
            timeout: 2,
            'padding-length': commonFlows[j].padding,
            dscp: commonFlows[j].dscp
          });
        }
      } else {
        const specificFlows =
          this.output[ConfigFieldMapping.SPECIFIC_FLOWS][twampReflector[i].ipAddress?.label];
        for (let j = 0; j < specificFlows.length; j++) {
          const flowName = this.getUniqueFlowName(
            twampReflector[i].name.label,
            specificFlows[j].dscp
          );
          config.push({
            'test-session-name': flowName,
            'sender-ip': this.output[ConfigFieldMapping.ADDRESS],
            'sender-udp-port': this.output[ConfigFieldMapping.BASE_UDP],
            'reflector-ip': twampReflector[i].ipAddress?.label,
            'reflector-udp-port': specificFlows[j].reflectorUDP,
            timeout: 2,
            'padding-length': specificFlows[j].padding,
            dscp: specificFlows[j].dscp
          });
        }
      }

      connectionInfo['twamp-session-request'] = config;
      multipleConnections.push(connectionInfo);
    }
    return multipleConnections;
  }

  //eslint-disable-next-line
  getSenderTestSessionConfig(twampReflector: any): any {
    const senderTestConfig = [];
    if (this.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
      const commonFlows = this.output[ConfigFieldMapping.COMMON_FLOWS];
      for (let i = 0; i < twampReflector.length; i++) {
        for (let j = 0; j < commonFlows.length; j++) {
          const flowName = this.getUniqueFlowName(
            twampReflector[i].name.label,
            commonFlows[j].dscp
          );
          const container = {};
          container['test-session-name'] = flowName;
          container['fill-mode'] = 'fill-mode.zero';
          container['periodic-interval'] = parseInt(commonFlows[j].txInterval);
          container['periodic-interval-units'] = 'milliseconds';
          senderTestConfig.push(container);
        }
      }
    } else {
      for (let j = 0; j < twampReflector.length; j++) {
        const specificFlows =
          this.output[ConfigFieldMapping.SPECIFIC_FLOWS][twampReflector[j].ipAddress?.label];
        specificFlows.forEach(item => {
          const flowName = this.getUniqueFlowName(twampReflector[j].name.label, item.dscp);
          const container = {};
          container['test-session-name'] = flowName;
          container['fill-mode'] = 'fill-mode.zero';
          container['periodic-interval'] = parseInt(item.txInterval);
          container['periodic-interval-units'] = 'milliseconds';
          senderTestConfig.push(container);
        });
      }
    }

    return senderTestConfig;
  }

  removeWindowAgentDevice(deviceData: Array<Device>): Array<Device> {
    const deviceTyoe = [AGENT_TYPE.WINDOWS];
    return deviceData.filter(data => {
      return !this.checkForSupportiveAgent(data, deviceTyoe);
    });
  }
  checkForSupportiveAgent(data: Device, deviceType: Array<AGENT_TYPE>): boolean {
    const platform = this.getParamsValueByName(data.deviceData.params, 'platform');
    const agentType = this.transformService.getAgentTypeBasedOnData(platform);
    return deviceType.includes(agentType);
  }
  // eslint-disable-next-line
  getParamsValueByName(params: any, name: string): any {
    const paramsObj = params.find(x => x.name === name);
    return paramsObj && paramsObj.value ? paramsObj.value : '';
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  setDeviceData(siteList: Site[]): any {
    const deviceList = this.entityCache
      .findEntityCache('device')
      .getValues()
      .filter(device => device.deviceData.type === 'GigaTestAgent');
    for (const site of siteList) {
      const deviceListForSite = deviceList.filter(
        device => device.deviceData.site === site.sitesData.id
      );
      let online = false,
        offline = false;
      if (deviceListForSite.length) {
        online = deviceListForSite.some(device => device.deviceData.status === 'online');
        offline =
          !online && deviceListForSite.some(device => device.deviceData.status === 'offline');
        site.sitesData.devices = this.getUpdatedDeviceData(deviceListForSite);
        site.sitesData.closed = true;
        site.sitesData.hidden = false;
        site.sitesData.status = online ? 'online' : offline ? 'offline' : '';
      }
    }
    return siteList;
  }
  getUpdatedDeviceData(data: Device[]): Device[] {
    for (let i = 0; i < data.length; i++) {
      const platform = data[i].deviceData.params.find(x => x.name === 'platform')
        ? data[i].deviceData.params.find(x => x.name === 'platform').value
        : '';
      data[i].deviceData.iconName = this.transformService.getIconDetail(
        data[i].deviceData.status,
        platform
      );
      data[i].deviceData['hidden'] = false;
    }
    return data;
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
  }

  setDestinationAgent(device: Device[]): void {
    this.selectedDestination = device;
  }

  getDestinationAgent(): Device[] {
    return this.selectedDestination;
  }

  getTwampTestConfig(): any {
    const testconfig = {};
    testconfig['interval'] = this.output[ConfigFieldMapping.PM_INTERVAL];
    testconfig['selectedTab'] = this.output[ConfigFieldMapping.SELECTED_TAB];
    testconfig['reflector-udp'] = this.output[ConfigFieldMapping.REFLECTOR_UDP];
    if (this.selectedDestination) {
      const testPointBInfo = [];
      this.selectedDestination.forEach(item => {
        testPointBInfo.push({
          tag: 'Test Point B',
          selected: false,
          name: 'Test Point B',
          type: 'inventory',
          vendor: 'viavi',
          version: '1.0',
          model: null,
          id: item.deviceData.id
        });
      });
      testconfig['selectedReflectedIds'] = testPointBInfo;
    }
    return testconfig;
  }

  getTestOptionsForSaveTemplate(): any {
    const testOptions = {};
    const listOfOptions = this.getOptions();
    listOfOptions.forEach(opt => {
      testOptions[opt.id] = opt.value;
    });
    return testOptions;
  }

  //eslint-disable-next-line
  getTxIntervalBasedAgent(): any {
    return this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_1G
      ? JMEP_TX_INTERVAL
      : TX_INTERVAL;
  }

  isDfBitOptDisabled(): boolean {
    return this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_1G ? true : false;
  }
}
